import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Mail, Lock, User, ArrowRight, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.password) {
            return toast.error('All fields are required');
        }
        if (form.password.length < 8) {
            return toast.error('Password must be at least 8 characters');
        }
        if (form.password !== form.confirm) {
            return toast.error('Passwords do not match');
        }

        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(`${API}/admin/join/${token}`, {
                name: form.name,
                email: form.email,
                password: form.password,
            });
            setSuccess(res.data);
            toast.success('Account created successfully!');
        } catch (err) {
            const detail = err.response?.data?.detail || 'Registration failed';
            setError(detail);
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-[#FAFAF5] flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200 p-8 text-center" data-testid="join-success">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-stone-900 mb-2">Welcome to {success.org_name}!</h1>
                    <p className="text-stone-500 mb-6">Your account has been created. You can now sign in with your credentials.</p>
                    <Button onClick={() => navigate('/login')} className="w-full bg-stone-900 hover:bg-stone-800" data-testid="join-go-login">
                        Go to Login <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAF5] flex items-center justify-center p-4">
            <div className="w-full max-w-md" data-testid="join-page">
                <div className="text-center mb-8">
                    <div className="w-12 h-12 bg-stone-900 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Users className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Join Your Team
                    </h1>
                    <p className="text-stone-500 text-sm mt-1">Create your account to get started</p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700" data-testid="join-error">
                        <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4" data-testid="join-form">
                    <div>
                        <Label>Full Name</Label>
                        <div className="relative mt-1">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <Input
                                value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="John Smith"
                                className="pl-9"
                                data-testid="join-name"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Email</Label>
                        <div className="relative mt-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <Input
                                type="email"
                                value={form.email}
                                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                placeholder="you@company.com"
                                className="pl-9"
                                data-testid="join-email"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Password</Label>
                        <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <Input
                                type="password"
                                value={form.password}
                                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                placeholder="Min 8 characters"
                                className="pl-9"
                                data-testid="join-password"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Confirm Password</Label>
                        <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <Input
                                type="password"
                                value={form.confirm}
                                onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                                placeholder="Re-enter password"
                                className="pl-9"
                                data-testid="join-confirm"
                            />
                        </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-stone-900 hover:bg-stone-800" data-testid="join-submit">
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                        Create Account
                    </Button>
                </form>

                <p className="text-center text-sm text-stone-400 mt-4">
                    Already have an account? <Link to="/login" className="text-stone-700 hover:underline">Sign in</Link>
                </p>
            </div>
        </div>
    );
}
